import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';

export default function Refund() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="bg-gradient-to-br from-purple-600 via-pink-600 to-purple-700 text-white py-10 sm:py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl font-extrabold mb-3">Refund Policy</h1>
          <p className="text-purple-100 text-sm sm:text-base max-w-xl mx-auto">
            Full transparency on refund conditions. Please read carefully before placing your order.
          </p>
          <p className="text-purple-200 text-xs mt-4">Last updated: May 2026</p>
        </div>
      </div>

      <main className="container mx-auto px-4 py-10 sm:py-16 max-w-4xl">

        <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-xl p-5 mb-10">
          <p className="font-semibold text-yellow-800 text-sm sm:text-base">
            ⚠️ Digital services are considered started as soon as the order is confirmed and payment is processed. By completing a purchase, the customer expressly accepts this refund policy.
          </p>
        </div>

        <div className="space-y-10 text-gray-700 text-sm sm:text-base leading-relaxed">

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-2 py-1 rounded-full">1</span>
              Nature of Digital Services
            </h2>
            <p className="mb-3">
              Services provided by GrowProPlus are digital in nature and executed immediately. Once delivery has begun following payment confirmation, the customer expressly waives their right of withdrawal applicable to digital content contracts.
            </p>
            <p>
              This waiver is communicated clearly during the order process and acceptance is a necessary condition for completing the purchase.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-2 py-1 rounded-full">2</span>
              When Refunds Are Not Available
            </h2>
            <p className="mb-3">As a general rule, refunds are <strong>not available</strong> once service delivery has begun. This includes situations where:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>The account was private or set to private during delivery.</li>
              <li>The username was changed after the order was placed.</li>
              <li>The account was suspended or deleted by the platform.</li>
              <li>The customer changed their mind after delivery began.</li>
              <li>Delivery is still in progress within the normal timeframe.</li>
              <li>The customer violated our Terms &amp; Conditions.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-2 py-1 rounded-full">3</span>
              Exceptional Cases — Refund May Apply
            </h2>
            <p className="mb-3">A full or partial refund may be considered in the following exceptional circumstances:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>The service was not delivered within twice the stated delivery timeframe.</li>
              <li>A technical error on our part prevented delivery entirely.</li>
              <li>Payment was charged twice for the same order.</li>
            </ul>
            <p className="mt-3">
              To request a refund under these circumstances, contact us at <a href="mailto:growproplus@growproplus.com" className="text-pink-600 hover:underline">growproplus@growproplus.com</a> within <strong>14 days</strong> of the order date with your order reference.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-2 py-1 rounded-full">4</span>
              Replenishment Guarantee
            </h2>
            <p className="mb-3">
              Instead of a refund for follower drops, GrowProPlus offers a <strong>30-day free replenishment</strong> guarantee. If your follower count drops below the delivered amount within 30 days due to natural platform fluctuations, we will replenish the difference at no cost.
            </p>
            <p>
              To request replenishment, contact us with your order reference and current follower count. Replenishment is not available if the account was made private or the username was changed.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-2 py-1 rounded-full">5</span>
              Refund Process
            </h2>
            <p className="mb-3">
              If a refund is approved, it will be processed to the original payment method (credit/debit card via Stripe) within <strong>5–10 business days</strong>.
            </p>
            <p>
              GrowProPlus reserves the right to deny refund requests that do not meet the criteria outlined in this policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-2 py-1 rounded-full">6</span>
              Chargebacks
            </h2>
            <p>
              Initiating a chargeback or payment dispute without first contacting GrowProPlus support constitutes a breach of these terms. We reserve the right to appeal all chargebacks with full evidence of service delivery.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-2 py-1 rounded-full">7</span>
              Contact
            </h2>
            <p>
              For any refund or replenishment request, contact us at:
              <br />
              📧 <a href="mailto:growproplus@growproplus.com" className="text-pink-600 hover:underline font-semibold">growproplus@growproplus.com</a>
            </p>
            <p className="mt-3">
              Please include your order reference number and a brief description of the issue.
            </p>
          </section>

        </div>
      </main>

      <footer className="bg-gray-900 text-white">
        <div className="container mx-auto px-4 py-8 text-center">
          <p className="text-gray-400 text-sm">© 2026 GrowProPlus. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
